import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { FISCAL_TABS } from '@/Pages/tabRoutes';
import ReconciliationPanel from './components/ReconciliationPanel';
import ConfigurationPanel from './components/ConfigurationPanel';

/**
 * §5 / ADR-0005 — Egypt fiscal compliance.
 *
 * Two tabs, reconciliation first. `docs/fiscal.md`'s one sentence is why:
 * "never make external authority availability the only source of local
 * transaction truth" — a café with a dead uplink keeps trading and
 * accumulates QUEUED documents, so the screen an operator needs open is the
 * one that shows what is pending, accepted, rejected and retrying. A rejected
 * document is presented there as an operator TASK, never as a failed sale,
 * and the fiscal document is never rendered as a property of a check or a
 * payment — it is its own aggregate, exactly as ADR-0005 §1 decided.
 *
 * Configuration is what a venue opens once, to set up the taxpayer identity,
 * the tax buckets and the item codes every future document is reported
 * under — and to read `GET /fiscal/configuration/validation`, the answer that
 * exists so a missing item code is found on a Tuesday afternoon and not while
 * a guest is waiting to pay.
 */
export default function Fiscal() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/fiscal', FISCAL_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('fiscal.title')}</h1>
        <p className="text-sm text-ink-muted">{t('fiscal.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('fiscal.title')}
        idPrefix="fiscal"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'reconciliation' && <ReconciliationPanel />}
      {current === 'configuration' && <ConfigurationPanel />}
    </div>
  );
}
